import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as SubframeUtils from "../../../../ui/utils";
import {
  SETTINGS_NAV_SECTIONS,
  SettingsNavItem,
  isSettingsNavItemActive,
  isSettingsNavItemVisible,
} from "../config/settingsNavigation";

interface NavLinkProps {
  item: SettingsNavItem;
  active: boolean;
  onSelect: (to: string) => void;
}

const NavLink: React.FC<NavLinkProps> = ({ item, active, onSelect }) => {
  const available = item.to !== undefined;

  return (
    <button
      type="button"
      disabled={!available}
      aria-current={active ? "page" : undefined}
      onClick={() => item.to && onSelect(item.to)}
      className={SubframeUtils.twClassNames(
        "flex w-full items-center rounded-md px-3 py-1.5 text-left text-body font-body transition-colors",
        {
          "bg-neutral-100 text-default-font": active,
          "text-subtext-color hover:bg-neutral-50 hover:text-default-font":
            available && !active,
          // Not-yet-built panels stay visible so the shape of the area is
          // legible, but must not look clickable.
          "cursor-not-allowed text-neutral-400 hover:bg-transparent": !available,
        }
      )}
    >
      {item.label}
    </button>
  );
};

/**
 * The list of settings panels.
 *
 * Groups the way the master onboarding board lists its steps. The first group
 * carries no heading — it sits directly under the area title, so a label there
 * would just repeat it.
 */
export const SettingsNav: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <nav className="flex w-full flex-col gap-6">
      {SETTINGS_NAV_SECTIONS.map((section) => {
        const visibleItems = section.items.filter(isSettingsNavItemVisible);
        if (visibleItems.length === 0) {
          return null;
        }

        return (
          <div key={section.id} className="flex flex-col gap-1">
            {section.label && (
              <span className="px-3 pb-1 text-caption-bold font-caption-bold uppercase tracking-wide text-subtext-color">
                {section.label}
              </span>
            )}
            {visibleItems.map((item) => (
              <NavLink
                key={item.id}
                item={item}
                active={isSettingsNavItemActive(item, pathname)}
                onSelect={navigate}
              />
            ))}
          </div>
        );
      })}
    </nav>
  );
};
